/**
 * Planning-doc markdown for Ryn's Roost (US 35621), parsed to a plain block model that the
 * web and mobile pages render with their own native text primitives.
 *
 * Nothing here produces HTML. Every piece of doc text stays a string that the page binds as
 * text (Angular interpolation, a NativeScript Span), so HTML or script in a doc can never
 * execute. Only a fixed subset of markdown is recognised; anything else is plain text.
 */

import { ADO_ORG, ADO_PROJECT, prHref } from './needs-you.logic';

export const ADO_WORK_ITEM_URL = `${ADO_ORG}/${ADO_PROJECT}/_workitems/edit/`;
/** The UPS1_Workspace pull request base, built by the wallboard's own `prHref`. */
export const ADO_PULL_REQUEST_URL = prHref(ADO_PROJECT, 0).replace(/0$/, '');

export type PlanningInline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'link'; text: string; href: string; external: boolean }
  /** WI 35683: an inline image whose src passed safeImageSrc. */
  | { kind: 'image'; alt: string; src: string }
  | { kind: 'strong' | 'em'; children: PlanningInline[] };

export type PlanningAttributionKind = 'decided' | 'proposal' | 'comment';

export interface PlanningAttribution {
  kind: PlanningAttributionKind;
  /** "Decided", "Proposal" or "Comment". */
  label: string;
  name: string;
}

/** One line of prose: optional scribe attribution, then its inline content. */
export interface PlanningLine {
  attribution: PlanningAttribution | null;
  inlines: PlanningInline[];
}

export type PlanningBlock =
  | {
      kind: 'heading';
      level: number;
      id: string;
      text: string;
      inlines: PlanningInline[];
    }
  | { kind: 'paragraph'; lines: PlanningLine[] }
  | { kind: 'list'; ordered: boolean; items: PlanningLine[] }
  | { kind: 'quote'; line: PlanningLine }
  | { kind: 'code'; text: string }
  | { kind: 'rule' }
  /** A GFM pipe table. Every row has exactly `align.length` cells; each cell is a line. */
  | {
      kind: 'table';
      align: PlanningCellAlign[];
      header: PlanningLine[];
      rows: PlanningLine[][];
    }
  /** A `:::columns` block: each column is its own run of blocks, shown side by side. */
  | { kind: 'columns'; columns: PlanningBlock[][] };

export type PlanningCellAlign = 'left' | 'center' | 'right' | null;

export interface PlanningSection {
  id: string;
  text: string;
  level: number;
}

export interface ParsedPlanningDoc {
  blocks: PlanningBlock[];
  sections: PlanningSection[];
}

const ATTRIBUTION = /^(Decided|Proposal|Comment) \(([^)\n]{1,80})\):?\s*/;

// One pass over the line, earliest match wins: code span, [label](url), **strong**, *em*, a ticket
// ref, then ![alt](src) (WI 35683; last so no earlier group renumbers, and only it can match at '!'). An em must not follow a word char or '*'; a ref must not follow a word char, '&', '#'
// or '/', so URL paths and "#12" stay text. Those "must not follow" rules are checked in code
// (precededBy), NOT with regex lookbehind: JavaScriptCore before iOS 16.4 rejects lookbehind at parse
// time and Roost's iOS floor is 15.5 (Bug 34650 class). Lookahead is fine there.
const INLINE =
  /`([^`\n]+)`|\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*(?![\w*])|(?:(WI|Bug|US|Task|Feature|PR)\s+#?(\d{2,7})|#(\d{3,7}))(?!\w)|!\[([^\]\n]*)\]\(([^)\s]+)\)/g;
const EM_BLOCKED_AFTER = /[\w*]/;
const REF_BLOCKED_AFTER = /[\w&#/]/;

function precededBy(text: string, index: number, blocked: RegExp): boolean {
  return index > 0 && blocked.test(text[index - 1]);
}

/** INLINE matches in order, skipping any em or ref whose previous character rules it out. A rejected
 * match resumes the scan one character later, exactly where a failed lookbehind would. */
function inlineMatches(text: string): RegExpExecArray[] {
  const pattern = new RegExp(INLINE.source, 'g');
  const found: RegExpExecArray[] = [];
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    const isEm = m[5] !== undefined;
    const isRef = m[6] !== undefined || m[8] !== undefined;
    if (
      (isEm && precededBy(text, m.index, EM_BLOCKED_AFTER)) ||
      (isRef && precededBy(text, m.index, REF_BLOCKED_AFTER))
    ) {
      pattern.lastIndex = m.index + 1;
      continue;
    }
    found.push(m);
  }
  return found;
}

/** Lowercase, runs of non-alphanumerics to one dash, trimmed; repeats get -2, -3. */
export function slugifyHeading(
  text: string,
  used: Map<string, number>
): string {
  const base =
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section';
  const seen = used.get(base) ?? 0;
  used.set(base, seen + 1);
  return seen === 0 ? base : `${base}-${seen + 1}`;
}

export function ticketUrl(kind: string | undefined, id: string): string {
  return kind === 'PR'
    ? prHref(ADO_PROJECT, Number(id))
    : `${ADO_WORK_ITEM_URL}${id}`;
}

/** Only web links, mail links and in-doc anchors survive; anything else stays text. */
export function safeHref(raw: string): string | null {
  const url = raw.trim();
  if (/^#[\w-]+$/.test(url)) return url;
  if (/^(https?:\/\/|mailto:)[^\s"'<>`]+$/i.test(url)) return url;
  return null;
}

/**
 * WI 35683 image allowlist: a base64 PNG or JPEG data URL, an https://quickchart.io/ chart, or (Task 37666) a UPS
 * PlantUML server SVG. Nothing
 * else becomes an image source: SVG (it can carry script), other data types, javascript:, plain http and
 * every other host stay text. The host must be followed by '/', so look-alikes
 * (quickchart.io.evil.com, quickchart.io@evil.com) fail. Bound with [src], never innerHTML.
 */
const DATA_IMAGE = /^data:image\/(?:png|jpeg);base64,[A-Za-z0-9+/]+={0,2}$/;
const QUICKCHART = /^https:\/\/quickchart\.io\/[^\s"'<>`\\]*$/;

/**
 * Task 37666: the UPS PlantUML server (the one RynBridge SendPuml.cs renders through). A ```plantuml block in a planning
 * doc becomes its /svg/<encoded> image; only that exact shape passes, never another path, a query or a public server.
 * An SVG loaded as an <img> runs no script.
 */
export const PLANTUML_SERVER_BASE =
  'https://plant-uml-server-upsllc.nicehill-64224cb8.southcentralus.azurecontainerapps.io';
const PLANTUML_SVG = new RegExp(
  `^${PLANTUML_SERVER_BASE.replace(/[.]/g, '\\.')}/svg/[0-9A-Za-z_-]+$`
);

export function safeImageSrc(raw: string): string | null {
  const src = raw.trim();
  return DATA_IMAGE.test(src) || QUICKCHART.test(src) || PLANTUML_SVG.test(src)
    ? src
    : null;
}

function pushText(out: PlanningInline[], text: string): void {
  if (!text) return;
  const last = out[out.length - 1];
  if (last?.kind === 'text') last.text += text;
  else out.push({ kind: 'text', text });
}

/** Inline markdown to tokens. Strong and em may hold links and code; nothing nests deeper. */
export function parseInline(
  text: string,
  allowEmphasis = true
): PlanningInline[] {
  const out: PlanningInline[] = [];
  let cursor = 0;
  for (const m of inlineMatches(text)) {
    const [whole, code, label, url, strong, em, kind, id, hashId, alt, src] = m;
    const start = m.index ?? 0;
    pushText(out, text.slice(cursor, start));
    cursor = start + whole.length;

    if (code !== undefined) {
      out.push({ kind: 'code', text: code });
    } else if (src !== undefined) {
      const safe = safeImageSrc(src);
      if (safe) out.push({ kind: 'image', alt: alt ?? '', src: safe });
      else pushText(out, whole);
    } else if (label !== undefined) {
      const href = safeHref(url);
      if (href)
        out.push({
          kind: 'link',
          text: label,
          href,
          external: !href.startsWith('#'),
        });
      else pushText(out, whole);
    } else if (strong !== undefined || em !== undefined) {
      if (allowEmphasis) {
        out.push({
          kind: strong !== undefined ? 'strong' : 'em',
          children: parseInline(strong ?? em, false),
        });
      } else {
        pushText(out, whole);
      }
    } else {
      const number = id ?? hashId;
      out.push({
        kind: 'link',
        text: kind ? `${kind} ${number}` : `#${number}`,
        href: ticketUrl(kind, number),
        external: true,
      });
    }
  }
  pushText(out, text.slice(cursor));
  return out;
}

export function parseLine(line: string): PlanningLine {
  const match = ATTRIBUTION.exec(line);
  if (!match) return { attribution: null, inlines: parseInline(line) };
  return {
    attribution: {
      kind: match[1].toLowerCase() as PlanningAttributionKind,
      label: match[1],
      name: match[2].trim(),
    },
    inlines: parseInline(line.slice(match[0].length)),
  };
}

/** Plain text of tokens, for screen readers, tests and mobile fallbacks. */
export function inlineText(inlines: PlanningInline[]): string {
  return inlines
    .map((t) =>
      'children' in t
        ? inlineText(t.children)
        : t.kind === 'image'
          ? t.alt
          : t.text
    )
    .join('');
}

/** One table row's cells: outer pipes dropped, split on pipes outside code spans, `\|` kept as a pipe. */
export function splitTableRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  const cells: string[] = [];
  let cell = '';
  let inCode = false;
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === '\\' && row[i + 1] === '|') {
      cell += '|';
      i++;
    } else if (ch === '`') {
      inCode = !inCode;
      cell += ch;
    } else if (ch === '|' && !inCode) {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += ch;
    }
  }
  cells.push(cell.trim());
  return cells;
}

const ALIGN_CELL = /^:?-+:?$/;

/** The `| --- | :-: |` row under a table header, as alignments; null when the line is not one. */
export function parseAlignRow(line: string): PlanningCellAlign[] | null {
  if (!line.includes('|')) return null;
  const cells = splitTableRow(line);
  if (!cells.every((c) => ALIGN_CELL.test(c))) return null;
  return cells.map((c) =>
    c.startsWith(':') && c.endsWith(':')
      ? 'center'
      : c.endsWith(':')
        ? 'right'
        : c.startsWith(':')
          ? 'left'
          : null
  );
}

/**
 * A table body row: holds a pipe and is not blank. As in GFM, a line that opens another block
 * (heading, quote, fence, list item, a ::: marker) ends the table even when it holds a pipe,
 * unless it starts with a pipe itself.
 */
const STARTS_BLOCK = /^\s*(?:#{1,6}\s|>|```|:::|[-*+]\s|\d+[.)]\s)/;

function isTableBodyRow(line: string): boolean {
  if (!line.includes('|') || !line.trim()) return false;
  return line.trimStart().startsWith('|') || !STARTS_BLOCK.test(line);
}

const COLUMNS_OPEN = /^\s*:::columns\s*$/;
const COLUMN_START = /^\s*:::col\s*$/;
const COLUMNS_CLOSE = /^\s*:::\s*$/;
const FENCE = /^\s*```/;

/**
 * The line ranges of a `:::columns` block opened at `open`: one range per column and the closing
 * line's index. Null when no `:::` closes it, or no `:::col` starts a column, so the caller keeps
 * the lines as plain text. Lines before the first `:::col` must be blank. Fenced code is skipped,
 * so a `:::` inside it closes nothing.
 */
function columnRanges(
  lines: string[],
  open: number
): { columns: [number, number][]; close: number } | null {
  const columns: [number, number][] = [];
  let start = -1;
  for (let i = open + 1; i < lines.length; i++) {
    const line = lines[i];
    if (FENCE.test(line)) {
      for (i++; i < lines.length && !FENCE.test(lines[i]); i++);
      continue;
    }
    if (COLUMNS_OPEN.test(line)) return null;
    if (COLUMN_START.test(line)) {
      if (start >= 0) columns.push([start, i]);
      start = i + 1;
    } else if (COLUMNS_CLOSE.test(line)) {
      if (start < 0) return null;
      columns.push([start, i]);
      return { columns, close: i };
    } else if (start < 0 && line.trim()) {
      return null;
    }
  }
  return null;
}

/** Blocks of `lines`; heading ids and sections are shared across columns so ids stay unique. */
function parseBlocks(
  lines: string[],
  sections: PlanningSection[],
  used: Map<string, number>,
  allowColumns: boolean
): PlanningBlock[] {
  const blocks: PlanningBlock[] = [];
  let paragraph: PlanningLine[] = [];
  let list: { ordered: boolean; items: PlanningLine[] } | null = null;

  const flushParagraph = (): void => {
    if (paragraph.length) blocks.push({ kind: 'paragraph', lines: paragraph });
    paragraph = [];
  };
  const flushList = (): void => {
    if (list) blocks.push({ kind: 'list', ...list });
    list = null;
  };
  const flushAll = (): void => {
    flushParagraph();
    flushList();
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (FENCE.test(line)) {
      flushAll();
      const code: string[] = [];
      for (i++; i < lines.length && !FENCE.test(lines[i]); i++)
        code.push(lines[i]);
      blocks.push({ kind: 'code', text: code.join('\n') });
      continue;
    }

    if (allowColumns && COLUMNS_OPEN.test(line)) {
      const ranges = columnRanges(lines, i);
      if (ranges) {
        flushAll();
        blocks.push({
          kind: 'columns',
          columns: ranges.columns.map(([from, to]) =>
            parseBlocks(lines.slice(from, to), sections, used, false)
          ),
        });
        i = ranges.close;
        continue;
      }
    }

    const heading = /^(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    if (heading) {
      flushAll();
      const text = heading[2];
      const id = slugifyHeading(text, used);
      const level = heading[1].length;
      sections.push({ id, text, level });
      blocks.push({
        kind: 'heading',
        level,
        id,
        text,
        inlines: parseInline(text),
      });
      continue;
    }

    const align =
      line.includes('|') && i + 1 < lines.length
        ? parseAlignRow(lines[i + 1])
        : null;
    const header = align ? splitTableRow(line) : null;
    if (align && header && header.length === align.length) {
      flushAll();
      const width = align.length;
      const fit = (cells: string[]): PlanningLine[] =>
        Array.from({ length: width }, (_, c) => parseLine(cells[c] ?? ''));
      const rows: PlanningLine[][] = [];
      for (i += 2; i < lines.length && isTableBodyRow(lines[i]); i++)
        rows.push(fit(splitTableRow(lines[i])));
      i--;
      blocks.push({ kind: 'table', align, header: fit(header), rows });
      continue;
    }

    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      flushAll();
      blocks.push({ kind: 'rule' });
      continue;
    }

    const item = /^\s*(?:([-*+])|(\d+)[.)])\s+(.*)$/.exec(line);
    if (item) {
      flushParagraph();
      const ordered = !item[1];
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push(parseLine(item[3]));
      continue;
    }

    const quote = /^\s*>\s?(.*)$/.exec(line);
    if (quote) {
      flushAll();
      blocks.push({ kind: 'quote', line: parseLine(quote[1]) });
      continue;
    }

    if (!line.trim()) {
      flushAll();
      continue;
    }

    flushList();
    paragraph.push(parseLine(line.trim()));
  }
  flushAll();
  return blocks;
}

export function parsePlanningMarkdown(
  markdown: string | null | undefined
): ParsedPlanningDoc {
  const lines = (markdown ?? '').replace(/\r\n?/g, '\n').split('\n');
  const sections: PlanningSection[] = [];
  const blocks = parseBlocks(lines, sections, new Map(), true);
  return { blocks, sections };
}
